import React, { useState, useEffect } from 'react';
import { profilesApi, permissionsApi } from '../api';
import { toast } from 'sonner';
import { extractApiError } from '../utils/dateValidation';
import {
  Shield,
  Plus,
  Search,
  Edit,
  Trash2,
  X,
  Loader2,
  CheckCircle,
  XCircle,
  ChevronDown,
  ChevronRight,
  Users,
} from 'lucide-react';

const Profiles = () => {
  const [profiles, setProfiles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    is_active: true,
    is_admin: false,
    permissions: [],
  });
  const [expandedModules, setExpandedModules] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [profilesRes, permissionsRes] = await Promise.all([
        profilesApi.list(),
        permissionsApi.grouped(),
      ]);
      setProfiles(profilesRes.data);
      setPermissions(permissionsRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const loadProfileDetails = async (profileId) => {
    try {
      const response = await profilesApi.get(profileId);
      return response.data;
    } catch (error) {
      toast.error('Erro ao carregar detalhes do perfil');
      return null;
    }
  };

  const handleOpenModal = async (profile = null) => {
    if (profile) {
      const fullProfile = await loadProfileDetails(profile.id);
      if (fullProfile) {
        setEditingProfile(fullProfile);
        
        // Map permissions to form data
        const permissionMap = {};
        (fullProfile.permissions || []).forEach(p => {
          permissionMap[p.permission_id] = {
            can_view: p.can_view,
            can_create: p.can_create,
            can_edit: p.can_edit,
            can_delete: p.can_delete,
            can_approve: p.can_approve,
          };
        });
        
        setFormData({
          name: fullProfile.name,
          description: fullProfile.description || '',
          is_active: fullProfile.is_active,
          is_admin: fullProfile.is_admin,
          permissions: permissionMap,
        });
      }
    } else {
      setEditingProfile(null);
      setFormData({
        name: '',
        description: '',
        is_active: true,
        is_admin: false,
        permissions: {},
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingProfile(null);
    setFormData({
      name: '',
      description: '',
      is_active: true,
      is_admin: false,
      permissions: {},
    });
    setExpandedModules([]);
  };

  const toggleModule = (moduleCode) => {
    setExpandedModules(prev =>
      prev.includes(moduleCode)
        ? prev.filter(m => m !== moduleCode)
        : [...prev, moduleCode]
    );
  };

  const togglePermission = (permissionId, action) => {
    setFormData(prev => {
      const currentPerm = prev.permissions[permissionId] || {
        can_view: false,
        can_create: false,
        can_edit: false,
        can_delete: false,
        can_approve: false,
      };
      
      return {
        ...prev,
        permissions: {
          ...prev.permissions,
          [permissionId]: {
            ...currentPerm,
            [action]: !currentPerm[action],
          },
        },
      };
    });
  };

  const toggleAllForPermission = (permissionId, value) => {
    setFormData(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [permissionId]: {
          can_view: value,
          can_create: value,
          can_edit: value,
          can_delete: value,
          can_approve: value,
        },
      },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name) {
      toast.error('Nome do perfil é obrigatório');
      return;
    }
    
    setSaving(true);
    
    try {
      // Convert permissions object to array
      const permissionsArray = Object.entries(formData.permissions)
        .filter(([_, actions]) => Object.values(actions).some(v => v))
        .map(([permissionId, actions]) => ({
          permission_id: permissionId,
          ...actions,
        }));
      
      const data = {
        name: formData.name,
        description: formData.description,
        is_active: formData.is_active,
        is_admin: formData.is_admin,
        permissions: permissionsArray,
      };
      
      if (editingProfile) {
        await profilesApi.update(editingProfile.id, data);
        toast.success('Perfil atualizado com sucesso!');
      } else {
        await profilesApi.create(data);
        toast.success('Perfil criado com sucesso!');
      }
      
      handleCloseModal();
      loadData();
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao salvar perfil'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (profile) => {
    if (!window.confirm(`Deseja realmente excluir o perfil "${profile.name}"?`)) {
      return;
    }
    
    try {
      await profilesApi.delete(profile.id);
      toast.success('Perfil excluído com sucesso!');
      loadData();
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao excluir perfil'));
    }
  };

  const filteredProfiles = profiles.filter(profile =>
    profile.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="profiles-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
            <Shield className="text-green-500" />
            Perfis de Acesso
          </h1>
          <p className="text-gray-400 mt-1">Gerenciar perfis e permissões do sistema</p>
        </div>
        <button
          onClick={() => handleOpenModal()}
          className="btn-primary flex items-center gap-2"
          data-testid="add-profile-btn"
        >
          <Plus size={20} />
          Novo Perfil
        </button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
        <input
          type="text"
          placeholder="Buscar perfis..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="input pl-10"
          data-testid="search-profiles"
        />
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="animate-spin text-green-500" size={32} />
          </div>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Descrição</th>
                  <th>Usuários</th>
                  <th>Admin</th>
                  <th>Status</th>
                  <th className="text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                {filteredProfiles.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center py-8 text-gray-500">
                      Nenhum perfil encontrado
                    </td>
                  </tr>
                ) : (
                  filteredProfiles.map((profile) => (
                    <tr key={profile.id}>
                      <td className="font-medium text-white">{profile.name}</td>
                      <td className="max-w-xs truncate">{profile.description || '-'}</td>
                      <td>
                        <span className="flex items-center gap-1 text-gray-400">
                          <Users size={16} />
                          {profile.user_count || 0}
                        </span>
                      </td>
                      <td>
                        {profile.is_admin ? (
                          <span className="badge badge-yellow">Sim</span>
                        ) : (
                          <span className="badge badge-gray">Não</span>
                        )}
                      </td>
                      <td>
                        {profile.is_active ? (
                          <span className="badge badge-green flex items-center gap-1 w-fit">
                            <CheckCircle size={14} />
                            Ativo
                          </span>
                        ) : (
                          <span className="badge badge-red flex items-center gap-1 w-fit">
                            <XCircle size={14} />
                            Inativo
                          </span>
                        )}
                      </td>
                      <td>
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleOpenModal(profile)}
                            className="p-2 text-gray-400 hover:text-blue-400 hover:bg-gray-700 rounded-lg transition-colors"
                            title="Editar"
                          >
                            <Edit size={18} />
                          </button>
                          <button
                            onClick={() => handleDelete(profile)}
                            className="p-2 text-gray-400 hover:text-red-400 hover:bg-gray-700 rounded-lg transition-colors"
                            title="Excluir"
                            disabled={profile.is_admin}
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <>
          <div className="modal-backdrop" />
          <div className="modal-content p-6 max-w-4xl">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white">
                {editingProfile ? 'Editar Perfil' : 'Novo Perfil'}
              </h2>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-white"
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Nome do Perfil *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="input"
                    placeholder="Ex: Auditor da Qualidade"
                    data-testid="profile-name-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Descrição
                  </label>
                  <input
                    type="text"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="input"
                    placeholder="Descrição do perfil"
                  />
                </div>
              </div>

              <div className="flex gap-6">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="w-5 h-5 rounded border-gray-600 bg-gray-700 text-green-500 focus:ring-green-500"
                  />
                  <span className="text-sm font-medium text-gray-300">Perfil Ativo</span>
                </label>

                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_admin}
                    onChange={(e) => setFormData({ ...formData, is_admin: e.target.checked })}
                    className="w-5 h-5 rounded border-gray-600 bg-gray-700 text-yellow-500 focus:ring-yellow-500"
                  />
                  <span className="text-sm font-medium text-gray-300">Administrador (acesso total)</span>
                </label>
              </div>

              {/* Permissions */}
              {!formData.is_admin && (
                <div>
                  <h3 className="text-lg font-semibold text-white mb-4">Permissões</h3>
                  <div className="space-y-2 max-h-96 overflow-y-auto pr-2">
                    {permissions.map((module) => (
                      <div key={module.module_code} className="border border-gray-700 rounded-lg overflow-hidden">
                        {/* Module Header */}
                        <button
                          type="button"
                          onClick={() => toggleModule(module.module_code)}
                          className="w-full flex items-center justify-between p-3 bg-gray-800 hover:bg-gray-750 transition-colors"
                        >
                          <span className="font-medium text-white">{module.module_name}</span>
                          {expandedModules.includes(module.module_code) 
                            ? <ChevronDown size={20} className="text-gray-400" />
                            : <ChevronRight size={20} className="text-gray-400" />
                          }
                        </button>

                        {/* Module Permissions */}
                        {expandedModules.includes(module.module_code) && (
                          <div className="p-3 bg-gray-900 space-y-3">
                            {/* Main Module Permission */}
                            {module.id && (
                              <PermissionRow
                                label="Acesso ao Módulo"
                                permissionId={module.id}
                                permissions={formData.permissions[module.id] || {}}
                                onToggle={togglePermission}
                                onToggleAll={toggleAllForPermission}
                              />
                            )}

                            {/* Tab Permissions */}
                            {module.tabs?.map((tab) => (
                              <PermissionRow
                                key={tab.id}
                                label={tab.tab_name}
                                permissionId={tab.id}
                                permissions={formData.permissions[tab.id] || {}}
                                onToggle={togglePermission}
                                onToggleAll={toggleAllForPermission}
                                isTab
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="btn-secondary"
                  disabled={saving}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="btn-primary flex items-center gap-2"
                  disabled={saving}
                  data-testid="save-profile-btn"
                >
                  {saving ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      Salvando...
                    </>
                  ) : (
                    'Salvar'
                  )}
                </button>
              </div>
            </form>
          </div>
        </>
      )}
    </div>
  );
};

const PermissionRow = ({ label, permissionId, permissions, onToggle, onToggleAll, isTab = false }) => {
  const allChecked = permissions.can_view && permissions.can_create && permissions.can_edit && permissions.can_delete && permissions.can_approve;
  const someChecked = permissions.can_view || permissions.can_create || permissions.can_edit || permissions.can_delete || permissions.can_approve;

  return (
    <div className={`flex items-center justify-between py-2 ${isTab ? 'pl-4 border-l-2 border-gray-700' : ''}`}>
      <div className="flex items-center gap-3">
        <input
          type="checkbox"
          checked={allChecked}
          ref={(el) => el && (el.indeterminate = someChecked && !allChecked)}
          onChange={() => onToggleAll(permissionId, !allChecked)}
          className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-green-500 focus:ring-green-500"
        />
        <span className="text-sm text-gray-300">{label}</span>
      </div>
      <div className="flex items-center gap-4">
        <PermissionCheckbox
          label="Ver"
          checked={permissions.can_view || false}
          onChange={() => onToggle(permissionId, 'can_view')}
        />
        <PermissionCheckbox
          label="Criar"
          checked={permissions.can_create || false}
          onChange={() => onToggle(permissionId, 'can_create')}
        />
        <PermissionCheckbox
          label="Editar"
          checked={permissions.can_edit || false}
          onChange={() => onToggle(permissionId, 'can_edit')}
        />
        <PermissionCheckbox
          label="Excluir"
          checked={permissions.can_delete || false}
          onChange={() => onToggle(permissionId, 'can_delete')}
        />
        <PermissionCheckbox
          label="Aprovar"
          checked={permissions.can_approve || false}
          onChange={() => onToggle(permissionId, 'can_approve')}
        />
      </div>
    </div>
  );
};

const PermissionCheckbox = ({ label, checked, onChange }) => (
  <label className="flex items-center gap-1 cursor-pointer">
    <input
      type="checkbox"
      checked={checked}
      onChange={onChange}
      className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-green-500 focus:ring-green-500"
    />
    <span className="text-xs text-gray-400">{label}</span>
  </label>
);

export default Profiles;
